import type {
  DataSource,
  FieldDefinition,
  ListQuery,
  StockByWarehouseRow,
  WarehouseStockTotal,
} from "@estelle/domain";

import { MAX_ROWS, PAGE_SIZE } from "@/components/table/export";
import { quantity, type Measure } from "@/modules/inventory/byWarehouse";
import type { ExportTable, Projection } from "@/modules/types";

/**
 * Stock Levels' Export with every warehouse chosen (#461): a row per variant,
 * a column per warehouse, and a total.
 *
 * The table lists a row per variant per warehouse, which a spreadsheet can pivot
 * but the team would rather not. Two blocks of columns, Available then On hand
 * (`byWarehouse.quantity`'s own definition), because the table no longer has a
 * toggle to say which one the file means.
 */

const MEASURES: { measure: Measure; label: string }[] = [
  { measure: "available", label: "Available" },
  { measure: "onHand", label: "On hand" },
];

const TEXT: FieldDefinition[] = [
  { name: "product", label: "Product", type: "text" },
  { name: "variant", label: "Variant", type: "text" },
  { name: "sku", label: "SKU", type: "text" },
];

/**
 * The file for these variants.
 *
 * A warehouse that stocks none of them is not a column of blanks, which is what
 * `listByWarehouse` reports with zero variants. A warehouse that does not hold
 * one variant is an empty cell and not a 0: zero is a count of nothing on the
 * shelf, and that is a claim this file should not make for a shelf that does
 * not exist.
 */
export function warehouseExportTable(
  rows: readonly StockByWarehouseRow[],
  warehouses: readonly WarehouseStockTotal[],
): ExportTable {
  const stocked = warehouses.filter((warehouse) => warehouse.variants > 0);

  const columns: FieldDefinition[] = [...TEXT];
  for (const { measure, label } of MEASURES) {
    for (const warehouse of stocked) {
      columns.push({
        name: `${measure}:${warehouse.id}`,
        label: `${label} (${warehouse.code})`,
        type: "number",
      });
    }
    columns.push({
      name: `${measure}:total`,
      label: `${label} (total)`,
      type: "number",
    });
  }

  const projected = rows.map((row): Projection => {
    const out: Projection = {
      product: row.product.title,
      variant: row.variant.title,
      sku: row.variant.sku,
    };
    for (const { measure } of MEASURES) {
      for (const warehouse of stocked) {
        const cell = row.cells[warehouse.id];
        out[`${measure}:${warehouse.id}`] = cell
          ? quantity(cell, measure)
          : null;
      }
      out[`${measure}:total`] = quantity(row.total, measure);
    }
    return out;
  });

  return { columns, rows: projected };
}

/**
 * Every variant under the query, not the page on screen, followed to the end
 * and capped the way `collectAll` is.
 */
export async function collectWarehouseExport(
  data: DataSource,
  query: ListQuery,
): Promise<ExportTable> {
  const rows: StockByWarehouseRow[] = [];
  let warehouses: WarehouseStockTotal[] = [];
  let cursor = query.cursor;

  while (rows.length < MAX_ROWS) {
    const page = await data.inventory.listByWarehouse({
      ...query,
      limit: PAGE_SIZE,
      cursor,
    });
    // The warehouse totals describe the whole filtered set on every page.
    warehouses = page.warehouses;
    rows.push(...page.items);
    if (!page.nextCursor || page.items.length === 0) break;
    cursor = page.nextCursor;
  }

  return warehouseExportTable(rows, warehouses);
}
